import { motion } from "framer-motion";
import { Download, FileText, Infinity as InfinityIcon, Layers } from "lucide-react";
import { useLang, useContent, loc, UI, BENTO } from "@/lib/content";
import { resolveImg } from "@/lib/api";

const ICONS = { layers: Layers, file: FileText, download: Download, infinity: InfinityIcon };

function BigCard({ item, delay }) {
  const { slot } = useContent();
  const { lang } = useLang();
  const img = slot(item.img);
  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay }}
      data-testid={`bento-${item.id}`}
      className="group relative h-72 overflow-hidden rounded-3xl border border-line shadow-sm sm:h-80 lg:col-span-2 lg:row-span-2 lg:h-full lg:min-h-[26rem]"
    >
      {img.visible && img.url ? (
        <img src={resolveImg(img.url)} alt={loc(item.title, lang)} data-testid={`bento-img-${item.img}`} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : (
        <div className="absolute inset-0 bg-navy" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/35 to-transparent" />
      <div className="relative flex h-full flex-col justify-end p-7 text-paper">
        <span className="w-fit rounded-full border border-brass/40 bg-navy/60 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-brass">
          {loc(item.tag, lang)}
        </span>
        <h3 className="mt-3 font-display text-2xl font-semibold sm:text-3xl">{loc(item.title, lang)}</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-paper/80">{loc(item.desc, lang)}</p>
      </div>
    </motion.article>
  );
}

function SmallCard({ item, delay }) {
  const { lang } = useLang();
  const Icon = ICONS[item.icon] || Layers;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay }}
      data-testid={`bento-${item.id}`}
      className="flex h-full min-h-[11rem] flex-col justify-between rounded-3xl border border-line bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-heritage/10 text-heritage">
        <Icon size={18} />
      </span>
      <div>
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-mutedtext">{loc(item.tag, lang)}</span>
        <h3 className="mt-1 text-base font-bold text-navy">{loc(item.title, lang)}</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-inkbody">{loc(item.desc, lang)}</p>
      </div>
    </motion.article>
  );
}

export default function Bento() {
  const { lang } = useLang();
  return (
    <section id="contenido" className="bg-paperdeep py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">{loc(UI.bentoHeader.overline, lang)}</p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {loc(UI.bentoHeader.title, lang)}
            <span className="italic text-heritage">{loc(UI.bentoHeader.titleAccent, lang)}</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {BENTO.map((item, i) =>
            item.big ? <BigCard key={item.id} item={item} delay={(i % 3) * 0.08} /> : <SmallCard key={item.id} item={item} delay={(i % 3) * 0.08} />
          )}
        </div>
      </div>
    </section>
  );
}
